"use client";

import React, { useEffect, useRef, useState } from "react";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";

const testimonials = [
  {
    quote: "Their response time has been fantastic, 24/7, at times when our business has needed a quick IT solution, PRO IT are always there to assist and get the job done. We happily recommend them.",
    name: "Jason Buckley",
    title: "Managing Director",
    company: "helloworld Travel",
    locations: "Milford · Browns Bay · Henderson · Ponsonby",
  },
  {
    quote: "Pro IT spent time analysing our infrastructure and issues and then prepared various recommendations for an optimal configuration of our Network, Hardware and Software. Work was completed in a timely fashion on budget. Their service is excellent, the response times are always great, and their ability to translate sometimes confusing IT jargon into easy to understand language.",
    name: "Phillip Norcross",
    title: "Director",
    company: "Copy Print",
  },
  {
    quote: "Over recent years, Amrit and his team have guided us through several important decisions on ICT, fibre and cloud solutions. These wise investments have reduced our costs, improved efficiency, and provided peace of mind. Pro IT understands that they aren't just making our network more secure — they are helping us provide a better quality of service to our clients.",
    name: "Deborah Benson",
    title: "Owner",
    company: "Word Sense",
  },
  {
    quote: "When our company moved offices I decided to review our IT services provider and decided to use Pro IT Solutions. They handled the move and subsequent setup of all IT hardware efficiently, professionally and in a timely manner. I have also been impressed with their suggestions of how we can work smarter and most cost effectively.",
    name: "Vickie Carson",
    title: "Director",
    company: "Global Events",
  },
  {
    quote: "Our migration was seamless and Amrit ensured no problem became an issue. He is extremely talented with providing solutions and the ease of contacting him is priceless. He is quick to respond and provide answers to queries or issues we may encounter. I would definitely recommend Amrit and his team at PRO IT for all IT Solutions.",
    name: "Deborah Kay",
    title: "Owner",
    company: "helloworld Travel Rotorua",
  },
  {
    quote: "Pro IT Solutions have proven to be extremely reliable and professional. They have worked with our company for many years and always provide solutions for all our IT work. We highly recommend this Company!",
    name: "Sue Hellesoe",
    title: "",
    company: "Eco Floors Ltd",
  },
  {
    quote: "We were fortunate to have met Amrit of Pro IT Solutions at the beginning of our venture and he has worked alongside us the whole way. Without his knowledge, skills and immediate response when contacted, we could not have managed and grown into the business we have today.",
    name: "John and Brenda",
    title: "",
    company: "Kudos Aluminium Louvres Ltd",
  },
];

const tickerPartners = [
  { name: "Microsoft",  logo: "/icons/partner-logos/microsoft.svg" },
  { name: "Cisco",      logo: "/icons/partner-logos/cisco.svg" },
  { name: "Dell",       logo: "/icons/partner-logos/dell.svg" },
  { name: "AWS",        logo: "/icons/partner-logos/aws.svg" },
  { name: "3CX",        logo: "/icons/partner-logos/threecx.svg" },
  { name: "Citrix",     logo: "/icons/partner-logos/citrix.svg" },
  { name: "HP",         logo: "/icons/partner-logos/hp.svg" },
  { name: "Lenovo",     logo: "/icons/partner-logos/lenovo.svg" },
  { name: "Ubiquiti",   logo: "/icons/partner-logos/ubiquiti.svg" },
  { name: "Fortinet",   logo: "/icons/partner-logos/fortinet.svg" },
];

const stats = [
  { value: "200+",   label: "Sites Deployed" },
  { value: "99.97%", label: "Uptime SLA" },
  { value: "0",      label: "Client Breaches" },
  { value: "500+",   label: "Businesses Served" },
];

const industries = [
  { label: "Retail Chains",         icon: "/icons/industry-icons/retail.svg" },
  { label: "Travel Agencies",       icon: "/icons/industry-icons/travel.svg" },
  { label: "Professional Services", icon: "/icons/industry-icons/professional.svg" },
  { label: "Healthcare",            icon: "/icons/industry-icons/healthcare.svg" },
  { label: "Hospitality",           icon: "/icons/industry-icons/hospitality.svg" },
  { label: "Property & Real Estate",icon: "/icons/industry-icons/property.svg" },
];

function StarRating() {
  return (
    <div className="flex gap-0.5 mb-4">
      {[...Array(5)].map((_, i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#FBBF24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

export default function SocialProofSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [visible, setVisible] = useState(false);

  // Show 3 cards at a time, cycling through all 7
  const cardsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / cardsPerPage);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setPage((prev) => (prev + 1) % totalPages);
    }, 7000);
    return () => clearInterval(interval);
  }, [visible, totalPages]);

  const currentCards = testimonials.slice(
    page * cardsPerPage,
    page * cardsPerPage + cardsPerPage
  );

  // Duplicate list for seamless infinite loop
  const loopItems = [...tickerPartners, ...tickerPartners];

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-24 lg:py-32"
      style={{ background: "rgba(255,255,255,0.04)" }}
    >
      <div className="max-w-container mx-auto px-4 sm:px-6 w-full">

        {/* Section header */}
        <FadeInView y={32} duration={0.6} delay={0}>
          <div className="text-center mb-16">
            <div className="section-sep mx-auto mb-5" />
            <h2
              className="font-heading font-700 leading-tight mb-4"
              style={{ fontSize: "clamp(28px, 3.5vw, 44px)", color: "#F8FAFC" }}
            >
              Trusted by New Zealand businesses
            </h2>
            <p className="text-[16px] font-body max-w-[480px] mx-auto" style={{ color: "#94A3B8" }}>
              Real results, real clients, real infrastructure — across the country.
            </p>
          </div>
        </FadeInView>

        {/* Stats row */}
        <StaggerContainer
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-12 sm:mb-16"
          staggerDelay={0.1}
          y={32}
        >
          {stats?.map((stat) => (
            <div
              key={stat?.label}
              className="rounded-card p-4 sm:p-6 text-center"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div
                className="font-heading font-700 leading-none mb-2"
                style={{ fontSize: "clamp(26px, 3vw, 38px)", color: "#60A5FA" }}
              >
                {stat?.value}
              </div>
              <div className="text-[14px] font-body" style={{ color: "#94A3B8" }}>{stat?.label}</div>
            </div>
          ))}
        </StaggerContainer>

        {/* Testimonial cards — 3 at a time */}
        <div className="mb-12 sm:mb-16">
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 items-stretch">
            {currentCards.map((t) => (
              <div
                key={t.name}
                className="rounded-card-lg p-4 sm:p-6 lg:p-8 flex flex-col h-full"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <StarRating />
                <blockquote
                  className="text-[15px] font-body leading-relaxed italic flex-1 mb-6"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="mt-auto flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-700 text-[13px] text-white shrink-0"
                    style={{ background: "#3B82F6" }}
                  >
                    {t.name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <div className="font-heading font-600 text-[14px]" style={{ color: "#F8FAFC" }}>
                      {t.name}
                    </div>
                    <div className="text-[13px] font-body" style={{ color: "#94A3B8" }}>
                      {t.title ? `${t.title}, ` : ""}{t.company}
                    </div>
                    {t.locations && (
                      <div className="text-[12px] font-body mt-0.5" style={{ color: "rgba(148,163,184,0.6)" }}>
                        {t.locations}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === page ? "24px" : "8px",
                  background: i === page ? "#3B82F6" : "rgba(255,255,255,0.15)",
                }}
                aria-label={`View testimonials page ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Side panel — Industries */}
        <FadeInView y={32} duration={0.6} delay={0.15}>
          <div
            className="rounded-card-lg p-6 mb-12 sm:mb-16 max-w-2xl mx-auto"
            style={{
              background: "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0.07) 100%)",
              border: "1px solid rgba(59,130,246,0.3)",
            }}
          >
            <div className="font-heading font-600 text-[15px] mb-4 text-center" style={{ color: "#F8FAFC" }}>
              Trusted by NZ businesses across:
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {industries?.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-center gap-2.5 text-[13px] font-heading font-500 py-2.5 px-3 rounded-lg text-center"
                  style={{
                    color: "#CBD5E1",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <img src={item.icon} alt={`${item.label} industry icon`} width={20} height={20} className="shrink-0" style={{ filter: "brightness(0) invert(0.75) sepia(1) saturate(3) hue-rotate(160deg)" }} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </FadeInView>
      </div>

      {/* ── Infinite Partner Ticker ── */}
      <FadeInView y={0} duration={0.6} delay={0.1}>
        <div
          className="w-full overflow-hidden"
          style={{
            background: "rgba(255,255,255,0.02)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {/* Label */}
          <div className="text-center pt-6 sm:pt-8 pb-4 sm:pb-5">
            <span
              className="text-[11px] font-body font-500 uppercase tracking-[0.2em]"
              style={{ color: "rgba(148,163,184,0.6)" }}
            >
              Trusted Technology Partners
            </span>
          </div>

          {/* Ticker track */}
          <div className="relative pb-6 sm:pb-8 overflow-hidden">

            {/* Left fade */}
            <div
              className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
              style={{
                background: "linear-gradient(to right, #070B14 0%, transparent 100%)",
              }}
            />
            {/* Right fade */}
            <div
              className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
              style={{
                background: "linear-gradient(to left, #070B14 0%, transparent 100%)",
              }}
            />

            {/* Scrolling row */}
            <div
              className="flex gap-5 sm:gap-8 w-max"
              style={{
                animation: "partnerTicker 30s linear infinite",
              }}
            >
              {loopItems.map((p, i) => (
                <div
                  key={`${p.name}-${i}`}
                  className="flex items-center justify-center w-[160px] sm:w-[180px] h-[48px] sm:h-[52px] rounded-full cursor-default select-none shrink-0"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <img
                    src={p.logo}
                    alt={p.name}
                    className="h-[28px] sm:h-[32px] max-w-[120px] sm:max-w-[140px] object-contain mx-auto opacity-50 hover:opacity-100 transition-opacity duration-300"
                    style={{ filter: "brightness(0) invert(0.85)" }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </FadeInView>

      {/* Keyframe injection */}
      <style>{`
        @keyframes partnerTicker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
